import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

import {
  Box,
  Button,
  Stack,
  Typography,
  Tooltip,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useCallback, useEffect, useState, useRef } from "react";
import ArticleIcon from "@mui/icons-material/Article";
import { CategoryOutlined } from "@mui/icons-material";
import { Document, Page, pdfjs } from "react-pdf";
import { useGetSourceDataRecord } from "src/views/datasets/services/DatasetServices";
import { useGetSDRArtifacts } from "src/views/datasets/services/DocumentServices";
import RefreshIcon from "@mui/icons-material/Refresh";
import DatasetViewerDownloadButton from "src/views/datasets/components/DatasetViewerDownloadButton";
import {
  useGetProposalGenerationDialogueData,
  getReportTypeDisplayName,
  stageDisplayConfig,
} from "../services/ProposalGenerationServices";
import { useGetStageConfig } from "../services/ProposalGenerationConfigService";
import { useProposalGenerationStore } from "../store/ProposalGenerationStore";
import { useIsPermissionDenied } from "src/helpers/Auth";
import { nuclearn_api_url } from "src/App";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import axios from "axios";

pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;

type ProposalGenerationReportPanelProps = {
  dialogueId: number | undefined;
  onIdentifyStages?: () => void;
  isIdentifyingStages?: boolean;
};

const ProposalGenerationReportPanel = ({
  dialogueId,
  onIdentifyStages,
  isIdentifyingStages,
}: ProposalGenerationReportPanelProps) => {
  const {
    setReportSdrId,
    reportType,
    sequenceNumber,
    stages,
    stageReportPrefix,
    stageReportSuffixLabel,
  } = useProposalGenerationStore();
  const { data: stageConfig } = useGetStageConfig();
  const identifyStagesDeniedByPermission =
    useIsPermissionDenied("artifact:read");
  const reportTypeDisplayName = getReportTypeDisplayName(
    reportType || undefined,
    sequenceNumber || undefined,
    stageDisplayConfig(
      stageReportPrefix,
      stageReportSuffixLabel,
      stageConfig?.effective,
    ),
  );

  const getIdentifyStagesButtonLabel = (): string => {
    if (isIdentifyingStages) {
      return (
        stageConfig?.effective.button_loading_label ||
        "Identifying Disciplines..."
      );
    }
    if (stages.length > 1) {
      return (
        stageConfig?.effective.button_done_label || "Disciplines Already Added"
      );
    }
    return stageConfig?.effective.button_label || "Identify Disciplines";
  };

  // Local state that tracks report SDR ID and file info
  const [reportSdrId, setLocalReportSdrId] = useState<number | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [file, setFile] = useState<any>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [markdownContent, setMarkdownContent] = useState<string | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [containerWidth, setContainerWidth] = useState(350);

  // Callback ref: sets up ResizeObserver when the container element mounts/unmounts.
  // This avoids the timing issue where useEffect([]) fires before the element exists.
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;

    const updateWidth = () => {
      const computedStyle = window.getComputedStyle(node);
      const horizontalPadding =
        parseFloat(computedStyle.paddingLeft) +
        parseFloat(computedStyle.paddingRight);
      const horizontalBorder =
        parseFloat(computedStyle.borderLeftWidth) +
        parseFloat(computedStyle.borderRightWidth);
      setContainerWidth(
        Math.max(0, node.offsetWidth - horizontalPadding - horizontalBorder),
      );
    };
    updateWidth();

    observerRef.current = new ResizeObserver(updateWidth);
    observerRef.current.observe(node);
  }, []);
  const [isFetchingFile, setIsFetchingFile] = useState(false);
  const [pdfLoadRetries, setPdfLoadRetries] = useState(0);
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const maxPdfLoadRetries = 5;

  // Reset all local state when dialogueId changes (navigating between stages)
  useEffect(() => {
    setLocalReportSdrId(null);
    setNumPages(null);
    setPageNumber(1);
    setFile(null);
    setFileType(null);
    setMarkdownContent(null);
    setIsFetchingFile(false);
    setPdfLoadRetries(0);
    setPdfLoadError(false);
    setReportSdrId(null);
  }, [dialogueId, setReportSdrId]);

  // Fetch proposal generation data to get the report SDR ID.
  // Refetching is driven by ProposalGenerationSidebar's artifact watcher
  // (which detects generate_report / modify_document / share_file completion);
  // React Query shares the cache by key so this hook receives the update.
  const { data: proposalData, isLoading: proposalDataIsLoading } =
    useGetProposalGenerationDialogueData(dialogueId);

  // Update reportSdrId when proposal data changes (handles both initial load and regeneration)
  useEffect(() => {
    if (
      proposalData?.report_sdr_id &&
      proposalData.report_sdr_id !== reportSdrId
    ) {
      console.log(
        "[ProposalGenerationReportPanel] Report SDR ID changed:",
        reportSdrId,
        "→",
        proposalData.report_sdr_id,
      );
      // Reset viewer state for the new report
      setFile(null);
      setFileType(null);
      setMarkdownContent(null);
      setNumPages(null);
      setPageNumber(1);
      setPdfLoadRetries(0);
      setPdfLoadError(false);

      setLocalReportSdrId(proposalData.report_sdr_id);
      setReportSdrId(proposalData.report_sdr_id);
    }
  }, [proposalData, reportSdrId, setReportSdrId]);

  // Get SDR info using report SDR ID
  const { data: sdrInfo, isLoading: sdrInfoIsLoading } = useGetSourceDataRecord(
    reportSdrId || undefined,
  );

  // Get artifacts for the SDR
  const {
    data: artifactsInfo,
    isLoading: artifactsInfoIsLoading,
    isError: artifactsLoadingIsError,
    refetch: refetchSDRArtifacts,
  } = useGetSDRArtifacts(sdrInfo?.id);

  // Track if we're still in the loading chain
  const isLoadingChain =
    (reportSdrId === null && proposalDataIsLoading) || // Fetching proposal data
    (reportSdrId && !sdrInfo && sdrInfoIsLoading !== false) ||
    (sdrInfo?.id && !artifactsInfo && artifactsInfoIsLoading) ||
    sdrInfoIsLoading ||
    artifactsInfoIsLoading;

  // Set up the file for PDF viewer or fetch markdown content
  useEffect(() => {
    if (artifactsInfo && !artifactsInfoIsLoading && !artifactsLoadingIsError) {
      if (artifactsInfo.length === 0) {
        console.log("[ProposalGenerationReportPanel] No artifacts found");
        return;
      }
      const firstArtifactInfo = artifactsInfo[0];
      const filename =
        firstArtifactInfo.filename || sdrInfo?.data?.file_name || "";
      const extension = filename.split(".").pop()?.toLowerCase();

      console.log("[ProposalGenerationReportPanel] Setting up file viewer:", {
        artifactId: firstArtifactInfo.id,
        filename,
        extension,
      });

      setFileType(extension || null);
      setIsFetchingFile(true);
      setPdfLoadRetries(0);
      setPdfLoadError(false);

      const downloadUrl = `${nuclearn_api_url}/artifacts/${firstArtifactInfo.id}/download-document`;
      console.log("[ProposalGenerationReportPanel] Download URL:", downloadUrl);

      // For markdown files, fetch the content as text
      if (extension === "md" || extension === "markdown") {
        axios
          .get(downloadUrl, { responseType: "text" })
          .then((response) => {
            console.log(
              "[ProposalGenerationReportPanel] Markdown fetched, content length:",
              response.data.length,
            );
            setMarkdownContent(response.data);
            setIsFetchingFile(false);
          })
          .catch((error) => {
            console.error("Error fetching markdown content:", error);
            setMarkdownContent(null);
            setIsFetchingFile(false);
          });
      } else {
        // For PDFs, DOCX, and other files, fetch as blob to avoid CORS issues
        refetchDocument(firstArtifactInfo.id, true);
      }
    }
  }, [artifactsInfo, artifactsInfoIsLoading, artifactsLoadingIsError, sdrInfo]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const refetchDocument = (artifactId: number, isInitial: boolean = false) => {
    const downloadUrl = `${nuclearn_api_url}/artifacts/${artifactId}/download-document`;
    axios
      .get(downloadUrl, { responseType: "blob" })
      .then((response) => {
        if (isInitial) {
          console.log("[ProposalGenerationReportPanel] Blob fetched:", {
            size: response.data.size,
            type: response.data.type,
            contentType: response.headers["content-type"],
            responseType: response.headers["content-type"] || "application/pdf",
          });
        }
        const headerContentType = response.headers["content-type"];
        const blob = new Blob([response.data], {
          type:
            typeof headerContentType === "string"
              ? headerContentType
              : "application/pdf",
        });
        if (isInitial) {
          console.log("[ProposalGenerationReportPanel] Created blob:", {
            size: blob.size,
            type: blob.type,
          });
        }
        setFile(blob);
        if (isInitial) {
          setPdfLoadRetries(0);
          setIsFetchingFile(false);
        }
      })
      .catch((err) => {
        console.error(
          "[ProposalGenerationReportPanel] Error refetching document:",
          err,
        );
        if (isInitial) {
          setFile(null);
          setIsFetchingFile(false);
        }
      });
  };

  // Refetch the report bytes. Client-scoped on purpose: pdfLoadError means the
  // automatic retries above could not render the PDF here, so the remedy is
  // another fetch — never reprocessing the SDR, which would delete and rebuild
  // its chunks and embeddings (see issue #2252).
  //
  // Fetches the blob directly rather than nudging the artifacts query: that
  // query is structurally shared, so a refetch returning identical data keeps
  // the same `artifactsInfo` reference and the setup effect never re-runs,
  // which would leave the panel blank. Only fall back to re-resolving the
  // artifact when there is no id to fetch.
  const handleRefreshPreview = useCallback(() => {
    const artifactId = artifactsInfo?.[0]?.id;
    setPdfLoadError(false);
    setPdfLoadRetries(0);
    if (artifactId == null) {
      setFile(null);
      void refetchSDRArtifacts();
      return;
    }
    setIsFetchingFile(true);
    refetchDocument(artifactId, true);
    // refetchDocument is redefined each render; depending on it would rebuild
    // this callback every time without changing behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactsInfo, refetchSDRArtifacts]);

  const handlePdfLoadError = (error: Error) => {
    console.error("[ProposalGenerationReportPanel] PDF load error:", {
      error,
      message: error?.message,
      fileSize: file?.size,
      fileType: file?.type,
      retryAttempt: pdfLoadRetries + 1,
    });

    // Retry loading the document up to maxPdfLoadRetries times
    if (pdfLoadRetries < maxPdfLoadRetries) {
      console.log(
        `[ProposalGenerationReportPanel] Retrying PDF load (attempt
        ${pdfLoadRetries + 1}/${maxPdfLoadRetries})...`,
      );
      setPdfLoadRetries((prev) => prev + 1);
      // Refetch the file after a 1 second delay with proper cleanup
      const timeoutId = setTimeout(() => {
        if (artifactsInfo && artifactsInfo.length > 0) {
          refetchDocument(artifactsInfo[0].id);
        }
      }, 1000);
      return () => clearTimeout(timeoutId);
    } else {
      console.error(
        "[ProposalGenerationReportPanel] Max PDF load retries exceeded",
      );
      setIsFetchingFile(false);
      setPdfLoadError(true);
    }
  };

  const changePage = (offset: number) => {
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
  };

  const previousPage = () => {
    changePage(-1);
  };

  const nextPage = () => {
    changePage(1);
  };

  // Show loading state while waiting for report generation or data fetch
  if (!reportSdrId) {
    return (
      <Box sx={{ px: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <ArticleIcon color="primary" />
              <Typography variant="h6">Generated Report</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your generated {reportTypeDisplayName.toLowerCase()} will appear
              here once ready
            </Typography>
          </Box>

          <Tooltip title="No report has been generated yet">
            <span>
              <Button
                variant="outlined"
                startIcon={<ArticleIcon />}
                disabled
                fullWidth
                size="large"
              >
                No Report Generated Yet
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    );
  }

  // Show document viewer once report is generated
  return (
    <Box
      ref={containerRef}
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        px: 2,
        pt: 1,
        pb: 2,
        boxSizing: "border-box",
      }}
    >
      <Stack spacing={2} sx={{ height: "100%" }}>
        {/* Header with title and download button */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", pb: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ minWidth: 0, flex: 1 }}
            >
              <ArticleIcon color="primary" sx={{ flexShrink: 0 }} />
              {sdrInfo?.dataset_fk && reportSdrId ? (
                <Typography
                  component="a"
                  href={`/datasets/${sdrInfo.dataset_fk}/document/${reportSdrId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="h6"
                  noWrap
                  sx={{
                    minWidth: 0,
                    color: "primary.main",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {sdrInfo?.data?.file_name || "Generated Report"}
                </Typography>
              ) : (
                <Typography variant="h6" noWrap sx={{ minWidth: 0 }}>
                  {sdrInfo?.data?.file_name || "Generated Report"}
                </Typography>
              )}
            </Stack>
            {sdrInfo && (
              <Box
                data-testid="propgen-btn-download-report"
                sx={{ display: "contents" }}
              >
                <DatasetViewerDownloadButton
                  row={{ sdrID: sdrInfo.id, data: sdrInfo.data }}
                />
              </Box>
            )}
          </Stack>
        </Box>

        {/* Document Viewer - PDF, Markdown, or other files */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {fileType === "md" || fileType === "markdown" ? (
            // Markdown Viewer
            markdownContent ? (
              <Box
                sx={{
                  width: "100%",
                  p: 2,
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                    margin: 0,
                  }}
                >
                  {markdownContent}
                </pre>
              </Box>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            )
          ) : file &&
            artifactsInfo &&
            artifactsInfo.length > 0 &&
            !pdfLoadError ? (
            // PDF Viewer
            <Document
              file={file}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={handlePdfLoadError}
              error={
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              }
              loading={
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              }
              noData={
                <Alert severity="warning">No document data was found.</Alert>
              }
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  border: "1px solid #e4e7eb",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  borderRadius: 1,
                  backgroundColor: "#f9f9f9",
                  mb: 2,
                }}
              >
                <Page
                  pageNumber={pageNumber}
                  width={containerWidth}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
              </Box>
            </Document>
          ) : isLoadingChain || artifactsInfoIsLoading || isFetchingFile ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : pdfLoadError ? (
            <Box sx={{ p: 1 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                Unable to load the document. Please try refreshing the page.
              </Alert>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshPreview}
                fullWidth
              >
                Refresh Preview
              </Button>
            </Box>
          ) : null}
        </Box>

        {/* Page Controls - Only show for PDF files */}
        {numPages &&
          numPages > 0 &&
          fileType !== "md" &&
          fileType !== "markdown" && (
            <Box
              sx={{
                borderTop: 1,
                borderColor: "divider",
                pt: 2,
                pb: 1,
                backgroundColor: "background.paper",
              }}
            >
              <Stack spacing={1}>
                <input
                  type="range"
                  min={1}
                  max={numPages}
                  value={pageNumber}
                  onChange={(e) => setPageNumber(Number(e.target.value))}
                  style={{
                    width: "100%",
                  }}
                />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <IconButton
                    onClick={previousPage}
                    disabled={pageNumber <= 1}
                    size="small"
                    color="primary"
                  >
                    <ArrowBackIosIcon fontSize="small" />
                  </IconButton>
                  <Typography
                    variant="body2"
                    data-testid="propgen-report-page-number"
                  >
                    Page {pageNumber} of {numPages}
                  </Typography>
                  <IconButton
                    onClick={nextPage}
                    disabled={pageNumber >= numPages}
                    size="small"
                    color="primary"
                  >
                    <ArrowForwardIosIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Box>
          )}

        {/* Identify Stages Button - only show on the first sequence (review report),
            and only when the configurable step is enabled */}
        {reportSdrId &&
          sequenceNumber === 1 &&
          stageConfig?.effective.enabled !== false && (
            <Box
              sx={
                stages.length <= 1 && !isIdentifyingStages
                  ? {
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      p: 2,
                      mt: 1,
                      mb: 2,
                    }
                  : {
                      borderTop: 1,
                      borderColor: "divider",
                      pt: 2,
                      pb: 2,
                    }
              }
            >
              {stages.length <= 1 && !isIdentifyingStages && (
                <Typography
                  variant="caption"
                  color="primary.main"
                  fontWeight={600}
                  sx={{
                    display: "block",
                    mb: 1,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Next Step
                </Typography>
              )}
              <Tooltip
                title={
                  identifyStagesDeniedByPermission
                    ? "User doesn't have permissions artifact:read"
                    : ""
                }
              >
                <span>
                  <Button
                    variant={stages.length > 1 ? "outlined" : "contained"}
                    color="primary"
                    startIcon={
                      isIdentifyingStages ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <CategoryOutlined />
                      )
                    }
                    onClick={onIdentifyStages}
                    disabled={
                      !onIdentifyStages ||
                      isIdentifyingStages ||
                      stages.length > 1 ||
                      identifyStagesDeniedByPermission
                    }
                    fullWidth
                    data-testid="propgen-btn-identify-stages"
                    size="large"
                    sx={{
                      ...(stages.length <= 1 &&
                        !isIdentifyingStages && {
                          fontWeight: 600,
                          py: 1.5,
                          fontSize: "0.95rem",
                        }),
                    }}
                  >
                    {getIdentifyStagesButtonLabel()}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
      </Stack>
    </Box>
  );
};

export default ProposalGenerationReportPanel;
